import { useImageCache } from '@pbk-web/state';
import type { MinimalUser } from '@pbk-web/types';
import { dataAttributeProps, useGetRandomProfilePicture } from '@pbk-web/utils';
import type { FC } from 'react';
import { useMemo } from 'react';

import Tooltip from '../tooltip/Tooltip';
import { ImageSize, ProfileSizeMap } from './ProfileAvatar';

type Props = {
  name?: string;
  users: MinimalUser[];
  size?: ImageSize;
  withMargin?: boolean;
  currentUserId?: string;
  [key: string]: unknown;
};

const isLocal = location.host.indexOf('localhost') !== -1;

const GroupAvatar: FC<Props> = (props) => {
  const { name, users, size = ImageSize.M, withMargin, currentUserId } = props;
  const { getImage } = useImageCache();
  const getRandomProfilePicture = useGetRandomProfilePicture();

  const data = useMemo<({ id: string; type: 'image' | 'fallback'; data: string | null; fullName: string } | null)[]>(() => {
    let source: ({ id: string; type: 'image' | 'fallback'; data: string | null; fullName: string } | null)[];

    if (isLocal) {
      source = users.map((x) => ({
        id: x.id,
        type: 'image' as const,
        data: x.userImageId ? getImage(x.userImageId) : getRandomProfilePicture(x.id),
        fullName: x.fullName ?? `${x.firstName} ${x.lastName}`,
      }));
    } else {
      const withImages = users
        .filter((x) => x.userImageId)
        .map((x) => ({ id: x.id, type: 'image' as const, data: getImage(x.userImageId!), fullName: x.fullName ?? `${x.firstName} ${x.lastName}` }));

      const fallbacks = users
        .filter((x) => !x.userImageId)
        .map((x) => ({
          id: x.id,
          type: 'fallback' as const,
          data: `${x.firstName?.[0] ?? ''}${x.lastName?.[0] ?? ''}`,
          fullName: x.fullName ?? `${x.firstName} ${x.lastName}`,
        }));

      source = (withImages as typeof source).concat(fallbacks as typeof source);
    }

    return source.concat([null, null, null, null]).slice(0, 4);
  }, [getImage, getRandomProfilePicture, users]);

  const sizeCss = ProfileSizeMap[size ?? ImageSize.M].image;
  const marginCss = withMargin ? '-ml-2' : '';
  const hoverCss = `[&:is([data-user-count]):not([data-user-count="0"]):not([data-user-count="1"])]:hover:-mt-[0.3rem] transition-all duration-300 ease-in-out hover:z-20`;

  return (
    <Tooltip text={name}>
      {(tooltip) => (
        <div
          {...tooltip}
          {...dataAttributeProps(props)}
          className={`z-0 grid grid-cols-2 grid-rows-2 items-center justify-center gap-[2px] overflow-clip rounded-full border border-white bg-white ${sizeCss} ${marginCss} ${hoverCss}`}
        >
          {data.map((x, i) => (
            <Tooltip key={i} text={!name && x?.fullName}>
              {(tooltip) => (
                <div {...tooltip} className="h-full w-full">
                  {x?.type === 'image' ? (
                    <div
                      className="h-full w-full"
                      style={{
                        backgroundImage: `url(${x.data ?? ''})`,
                        backgroundSize: '200% 200%',
                        backgroundRepeat: 'no-repeat',
                        backgroundPositionX: i % 2 === 0 ? '0%' : '100%',
                        backgroundPositionY: i < 2 ? '0%' : '100%',
                      }}
                    />
                  ) : (
                    <div
                      className={`text-pbk-12 h-full w-full text-center text-white select-none ${currentUserId === x?.id ? 'bg-greyscale-15' : x ? 'bg-accent-4-100' : 'bg-greyscale-15'}`}
                    >
                      {x?.data}
                    </div>
                  )}
                </div>
              )}
            </Tooltip>
          ))}
        </div>
      )}
    </Tooltip>
  );
};

export default GroupAvatar;
